import React, { forwardRef, useImperativeHandle } from 'react'

import { useTranslation } from 'react-i18next'

import CreditCardForm from '@components/commerce/CreditCardForm'
import SavedCustomerPayments from '@components/commerce/SavedCustomerPayments'
import UsernameForm from '@components/commerce/UsernameForm'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import { CustomerCardType } from '@models/commerce/CustomerCardType'
import Price from '@models/commerce/Price'
import { useTitle } from '@providers/TitleProvider'
import { Elements } from '@stripe/react-stripe-js'

import usePaymentForm, { FormState } from './hooks/usePaymentForm'

interface Props {
  ref: React.Ref<HTMLElement>
  clientSecret?: string
  price: Price
  setTaxApiParams: (val) => void // todo Type for tax API request
  onChangeValidity?: (val: boolean) => void
}

export interface PaymentFormRefType {
  first_name: string
  last_name: string
  onSubmitForm: () => Promise<CustomerCardType | undefined>
  isValid: boolean
}

const PaymentForm = forwardRef<PaymentFormRefType, Props>(
  ({ clientSecret, price, setTaxApiParams, onChangeValidity }, ref) => {
    const { t } = useTranslation()
    const { title } = useTitle()
    const {
      isValid,
      selectedCard,
      cards,
      uiState,
      stripePromise,
      formData,
      setFormData,
      token,
      setToken,
      setSelectedCard,
      onSubmitForm,
      transitionUiState,
    } = usePaymentForm({ price, setTaxApiParams, onChangeValidity })

    useImperativeHandle(
      ref,
      () => ({
        onSubmitForm,
        isValid,
        first_name: formData.first_name,
        last_name: formData.last_name,
      }),
      [isValid, uiState, selectedCard, token, formData]
    )

    if (!title.product) return null
    return (
      <div data-testid="payment-form">
        {uiState == FormState.PENDING && <LoadingSpinner />}
        {uiState == FormState.EXISTING_CARD && (
          <div data-testid="saved-customer-payments">
            <UsernameForm
              formData={formData}
              handleElementChange={({ name, value }) => setFormData({ [name]: value })}
            />
            <SavedCustomerPayments cards={cards} onSelectCard={setSelectedCard} />
            <div className="flex items-center my-3">
              <i className="pr-1 text-xl text-orange nf-icon nf-card"></i>
              <span
                className="cursor-pointer text-orange text-md "
                onClick={() => transitionUiState(FormState.NEW_CARD)}>
                {t('action.enterNewCard')}
              </span>
            </div>
          </div>
        )}
        {uiState == FormState.NEW_CARD && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CreditCardForm formData={formData} setFormData={setFormData} setToken={setToken} />
          </Elements>
        )}
      </div>
    )
  }
)

export default PaymentForm
